import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";

import AutoFormDialog from "../dialogTypes/AutoFormDialog";
import UpdateRecordButtonLayout from "../layout/UpdateRecordButtonLayout";
import validateConditionJsonStructure from "../../lib/utils/validateConditionJsonStructure";
import getSpecific from "../../lib/api/getSpecific";

/**
 * @COMPONENT_NAME MachineTypeStateTransitionUpdateButton
 * @DESCRIPTION 설비 유형별 상태 전이 데이터 수정을 위한 커스텀 버튼 컴포넌트
 *             선택된 상태 전이 데이터를 수정할 수 있는 다이얼로그를 표시
 *
 * @PROPS
 *   - {string} id - 컴포넌트 ID
 *   - {Object} schema - 테이블 스키마 정보
 *   - {Object} grid - 그리드 객체 참조
 *   - {Object} refresh - API refresh 객체
 *   - {Function} onSuccess - 성공 시 콜백
 *   - {boolean} disabled - 버튼 비활성화 여부
 *   - {Function} onSubmitCheck - 제출 전 유효성 검사 콜백
 *   - {...any} others - 기타 props
 * @CALLBACKS
 *   - {Function} onOpen - 버튼 클릭 시 호출되는 콜백
 *   - {Function} onClose - 다이얼로그 닫기 시 호출되는 콜백
 *   - {Function} onCreateSuccess - 생성 성공 시 호출되는 콜백
 *   - {Function} setInitialDataCallback - 초기 데이터 설정 콜백
 *   - {Function} checkFormValidationCallback - 폼 데이터 검증 콜백
 *   - {Function} setSchemaCallback - 스키마 설정 콜백
 *
 */
function MachineTypeStateTransitionUpdateButton(props) {
    const { t } = useTranslation();

    const { id, schema, grid, refresh, onSuccess, selectedMachineType, machineTypeContextDataNames, ...others } = props;

    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState();
    const [modifiedSchema, setModifiedSchema] = useState(schema);

    useEffect(() => {
        if (schema && selectedMachineType) {
            const newSchema = cloneDeep(schema);

            for (let column of newSchema.columns) {
                if (column.name === "prevState" || column.name === "nextState") {
                    if (column.codeInfo) {
                        column.codeInfo.tableName = `VwMachineTypeState/${selectedMachineType.machineTypeCode}`;
                    }
                }
            }

            setModifiedSchema(newSchema);
        }
    }, [schema, selectedMachineType]);

    const onOpen = async () => {
        const selectedItem = grid?.selectedItems[0];
        // 목록 뷰(VwMachineTypeStateTransition)는 상태 이름만 내므로, 폼이 고르는 상태 코드는 원본 행에서 읽는다
        const transition = await getSpecific("MachineTypeStateTransition", selectedItem?.stateTransitionId);
        setFormData({
            ...selectedItem,
            prevState: transition?.prevState,
            nextState: transition?.nextState,
        });
        setOpen(true);
    };

    const onClose = () => {
        setSchemaCallback(grid?.selectedItems[0]?.stateTransitionType);
        setOpen(false);
    };

    const onUpdateSuccess = (msg) => {
        if (onSuccess) {
            onSuccess(msg);
        }

        if (refresh) {
            refresh.refresh();
        }
    };

    const modifyFormDataCallback = (formDataToUpdate, name, value) => {
        if (name !== "stateTransitionType") {
            return;
        }

        // initialData(formData)를 바꾸면 PATCH 비교 기준이 바뀌어 고른 유형이 빠진다 — 칸 값만 비운다
        formDataToUpdate.condition = "";
        setSchemaCallback(formDataToUpdate.stateTransitionType);
    };

    const setSchemaCallback = (stateTransitionType) => {
        if (stateTransitionType === "EVENT") {
            setModifiedSchema((prevModifiedSchema) => {
                const newSchema = cloneDeep(prevModifiedSchema);
                for (let column of newSchema.columns) {
                    if (column.name === "condition") {
                        column.formItemType = "autocomplete";
                        column.codeInfo = {
                            tableName: `VwMachineTypeEvent/${selectedMachineType.machineTypeCode}`,
                            nameColumn: ["eventName", "description"],
                            valueColumn: "eventName",
                            useEmptyValue: false,
                            sortByName: true,
                        };
                    }
                }
                return newSchema;
            });
        } else if (stateTransitionType === "CONDITION") {
            setModifiedSchema((prevModifiedSchema) => {
                const newSchema = cloneDeep(prevModifiedSchema);
                for (let column of newSchema.columns) {
                    if (column.name === "condition") {
                        column.formItemType = "text";
                    }
                }
                return newSchema;
            });
        }
    };

    const checkFormValidationCallback = (formDataToUpdate) => {
        if (formDataToUpdate.stateTransitionType === "EVENT") {
            return [true, ""];
        }

        if (formDataToUpdate.stateTransitionType === "CONDITION") {
            try {
                validateConditionJsonStructure(formDataToUpdate.condition, machineTypeContextDataNames);
            } catch (error) {
                return [false, error.message];
            }
        }

        return [true, ""];
    };

    return (
        <>
            <UpdateRecordButtonLayout id={id + "update-button"} onClick={onOpen} {...others} />
            <AutoFormDialog
                id={id + "update-dialog"}
                mode="edit"
                formId={id + "update-form"}
                title={schema ? t(`dialog.title.${schema.name}.update`) : ""}
                schema={modifiedSchema}
                initialData={formData}
                open={open}
                onClose={onClose}
                onSuccess={onUpdateSuccess}
                modifyFormDataCallback={modifyFormDataCallback}
                checkFormValidationCallback={checkFormValidationCallback}
            />
        </>
    );
}

export default MachineTypeStateTransitionUpdateButton;
